<div class="min-h-screen w-full min-w-0 overflow-x-hidden bg-cloud-mist">
  <div class="tfr-page-shell py-6">

    <!-- Header -->
    <header class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="min-w-0">
        <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-action-blue">TimeFlexRent</p>
        <h1 class="mt-1 text-[26px] font-bold tracking-tight text-midnight-indigo sm:text-[28px]">
          Panel de administración
        </h1>
        <p class="mt-1 max-w-xl text-[13px] leading-relaxed text-steel-gray">
          Pulso de la plataforma y crecimiento en tiempo real.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button
          type="button"
          (click)="toggleLiveStatus()"
          [disabled]="statusSaving()"
          class="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-[12px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 disabled:opacity-60"
          [class.bg-emerald-50]="!isMaintenance()"
          [class.border-emerald-200]="!isMaintenance()"
          [class.text-emerald-800]="!isMaintenance()"
          [class.bg-amber-50]="isMaintenance()"
          [class.border-amber-300]="isMaintenance()"
          [class.text-amber-900]="isMaintenance()">
          <span
            class="h-2 w-2 rounded-full"
            [class.bg-emerald-500]="!isMaintenance()"
            [class.animate-pulse]="!isMaintenance()"
            [class.bg-amber-500]="isMaintenance()"></span>
          @if (statusSaving()) {
            Guardando&hellip;
          } @else {
            {{ statusLabel() }}
          }
        </button>
      </div>
    </header>

    <!-- KPI grid -->
    <section class="mb-6" aria-label="Indicadores clave">
      @if (kpiError(); as err) {
        <div class="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-800">
          <strong>Error cargando métricas:</strong> {{ err }}
        </div>
      }
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        @for (card of kpis(); track card.id) {
          <article class="tfr-kpi-card">
            <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">{{ card.label }}</p>
            @if (kpiLoading()) {
              <div class="mt-2 h-8 w-24 animate-pulse rounded-lg bg-pale-gray"></div>
              <div class="mt-2 h-3 w-36 animate-pulse rounded-lg bg-pale-gray"></div>
            } @else {
              <p class="mt-2 text-[28px] font-bold tabular-nums leading-none text-midnight-indigo">{{ card.value }}</p>
              <p class="mt-1.5 text-[11px] font-medium"
                 [class.text-emerald-700]="card.deltaPositive"
                 [class.text-red-600]="!card.deltaPositive">
                {{ card.deltaLabel }}
              </p>
            }
            <div class="tfr-kpi-sparkline mt-3">
              <canvas #sparkCanvas aria-hidden="true"></canvas>
            </div>
          </article>
        }
      </div>
    </section>

    <!-- Admin shortcuts -->
    <section class="mb-6" aria-label="Accesos directos de administración">
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">

        <a routerLink="/admin/email-templates"
           class="group flex flex-col items-start gap-3 rounded-2xl border border-pale-gray
                  bg-white px-4 py-4 transition hover:border-action-blue/30 hover:shadow-tfr-sm">
          <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-action-blue/10 text-action-blue
                       group-hover:bg-action-blue group-hover:text-white transition-colors">
            <lucide-icon name="mail" [size]="17" />
          </span>
          <div>
            <p class="text-[13px] font-semibold text-midnight-indigo leading-snug">Plantillas correo</p>
            <p class="text-[11px] text-steel-gray mt-0.5">Asuntos y contenidos</p>
          </div>
        </a>

        <a routerLink="/admin/billing"
           class="group flex flex-col items-start gap-3 rounded-2xl border border-pale-gray
                  bg-white px-4 py-4 transition hover:border-action-blue/30 hover:shadow-tfr-sm">
          <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-action-blue/10 text-action-blue
                       group-hover:bg-action-blue group-hover:text-white transition-colors">
            <lucide-icon name="receipt" [size]="17" />
          </span>
          <div>
            <p class="text-[13px] font-semibold text-midnight-indigo leading-snug">Facturación plataforma</p>
            <p class="text-[11px] text-steel-gray mt-0.5">Emisor, IVA e IRPF</p>
          </div>
        </a>

        <a routerLink="/admin/invoices"
           class="group flex flex-col items-start gap-3 rounded-2xl border border-pale-gray
                  bg-white px-4 py-4 transition hover:border-action-blue/30 hover:shadow-tfr-sm">
          <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-action-blue/10 text-action-blue
                       group-hover:bg-action-blue group-hover:text-white transition-colors">
            <lucide-icon name="file-text" [size]="17" />
          </span>
          <div>
            <p class="text-[13px] font-semibold text-midnight-indigo leading-snug">Facturas</p>
            <p class="text-[11px] text-steel-gray mt-0.5">Listar e imprimir</p>
          </div>
        </a>

        <a routerLink="/admin/settings"
           class="group flex flex-col items-start gap-3 rounded-2xl border border-pale-gray
                  bg-white px-4 py-4 transition hover:border-action-blue/30 hover:shadow-tfr-sm">
          <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-action-blue/10 text-action-blue
                       group-hover:bg-action-blue group-hover:text-white transition-colors">
            <lucide-icon name="file-text" [size]="17" />
          </span>
          <div>
            <p class="text-[13px] font-semibold text-midnight-indigo leading-snug">Plantilla global</p>
            <p class="text-[11px] text-steel-gray mt-0.5">Contrato por defecto</p>
          </div>
        </a>

        <a routerLink="/admin/amenities"
           class="group flex flex-col items-start gap-3 rounded-2xl border border-pale-gray
                  bg-white px-4 py-4 transition hover:border-action-blue/30 hover:shadow-tfr-sm">
          <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-action-blue/10 text-action-blue
                       group-hover:bg-action-blue group-hover:text-white transition-colors">
            <lucide-icon name="layers" [size]="17" />
          </span>
          <div>
            <p class="text-[13px] font-semibold text-midnight-indigo leading-snug">Equipamiento</p>
            <p class="text-[11px] text-steel-gray mt-0.5">Amenidades del wizard</p>
          </div>
        </a>

        <a routerLink="/admin/crm/leads"
           class="group flex flex-col items-start gap-3 rounded-2xl border border-pale-gray
                  bg-white px-4 py-4 transition hover:border-action-blue/30 hover:shadow-tfr-sm">
          <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-action-blue/10 text-action-blue
                       group-hover:bg-action-blue group-hover:text-white transition-colors">
            <lucide-icon name="users" [size]="17" />
          </span>
          <div>
            <p class="text-[13px] font-semibold text-midnight-indigo leading-snug">CRM prospección</p>
            <p class="text-[11px] text-steel-gray mt-0.5">Pipeline de propietarios</p>
          </div>
        </a>

      </div>
    </section>

  </div>
</div>
